import axios from 'axios'
import type { RefreshedSession } from '@ai-meeting/shared'
import { API_BASE } from './apiBase'
import { getRefreshToken, storeSessionTokens } from './session'
import { useStore } from '@/store/useStore'

/**
 * The refresh in flight, if any.
 *
 * Shared rather than started per caller. A meeting fires several requests at
 * once, so an expired access token produces a burst of 401s — and since every
 * refresh rotates the token, a second concurrent call would present the one the
 * first already spent and get itself signed out (BRAI-422).
 */
let inFlight: Promise<string | null> | null = null

/**
 * Spend the refresh token for a new access token, and store both.
 *
 * Uses a bare axios call, not `apiClient`: the client's own interceptor is what
 * calls this, and routing the refresh back through it would have a failing
 * refresh trigger another refresh.
 *
 * @returns The new access token, or null when the session cannot be renewed —
 *   no refresh token to spend, or the server refused the one there was.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inFlight) return inFlight

  inFlight = (async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) return null

    try {
      const { data } = await axios.post<RefreshedSession>(
        `${API_BASE}/api/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      )
      storeSessionTokens(data.accessToken, data.refreshToken)
      syncSessionUserFromRefresh(data)
      return data.accessToken
    } catch {
      return null
    }
  })().finally(() => {
    inFlight = null
  })

  return inFlight
}

function syncSessionUserFromRefresh(data: RefreshedSession): void {
  const currentUser = useStore.getState().user
  if (!currentUser) return

  useStore.getState().patchSessionUser({
    email: data.user.email,
    displayName: data.user.name,
    plan: data.user.role,
    role: data.user.role,
    authProvider: data.user.authProvider ?? 'local',
  })
}

/** Hydrate authProvider (and related fields) for sessions persisted before it existed. */
export function hydrateSessionProfile(): Promise<boolean> {
  const user = useStore.getState().user
  if (!user || user.authProvider) {
    return Promise.resolve(Boolean(user?.authProvider))
  }
  if (!getRefreshToken()) {
    return Promise.resolve(false)
  }

  return refreshAccessToken().then((token) => Boolean(token && useStore.getState().user?.authProvider))
}

/** Test seam: forget any refresh in flight. */
export function resetRefreshState(): void {
  inFlight = null
}
